import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

/**
 * Generates CSS custom properties for the base state of the textarea field.
 *
 * @param props       The properties object containing the theme.
 * @param props.theme The emotion theme object containing color tokens.
 * @returns CSS custom properties string for base textarea styling.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    --textarea-background-color: ${theme.colors.neutral20};
    --textarea-border-color: ${theme.colors.neutral20};
    --textarea-font-color: ${theme.colors.textPrimary};
    --textarea-placeholder-font-color: ${theme.colors.textSecondary};
    --label-font-color: ${theme.colors.textPrimary};
`;

/**
 * Generates CSS custom properties for the disabled state of the textarea field.
 *
 * @param props       The properties object containing the theme.
 * @param props.theme The emotion theme object containing color tokens.
 * @returns CSS custom properties string for disabled textarea styling.
 */
export const getDisabledVariables = ({theme}: VariablesProps) => `
    --textarea-font-color: ${theme.colors.neutral40};
    --textarea-placeholder-font-color: ${theme.colors.neutral40};
    --label-font-color: ${theme.colors.neutral60};
`;

/**
 * Generates CSS custom properties for the error state of the textarea field.
 *
 * @param props       The properties object containing the theme.
 * @param props.theme The emotion theme object containing color tokens.
 * @returns CSS custom properties string for error textarea styling.
 */
export const getErrorVariables = ({theme}: VariablesProps) => `
    --textarea-border-color: ${theme.colors.error};
    --label-font-color: ${theme.colors.textPrimary};
`;

/**
 * Generates CSS custom properties for the focus state of the textarea field.
 *
 * @param props       The properties object containing the theme.
 * @param props.theme The emotion theme object containing color tokens.
 * @returns CSS custom properties string for focused textarea styling.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    --textarea-background-color: ${theme.colors.neutral0};
    --textarea-border-color: ${theme.colors.primary};
    --label-font-color: ${theme.colors.primary};
`;